import type { ISocialOfferWithFavoriteChallenges, TSocialOfferCreationOrEdition } from "@api";
import type { Override } from "@bbj-types/utils";
import { CreateWithAIModal, type ICreateWithAIModalLabels } from "@complex/CreateWithAIModal";
import type { TContactValue } from "@complex/FormGen/Components/Inputs/Contact";
import type { TPerimeterOption } from "@complex/FormGen/Components/Inputs/Perimeter";
import type { TStructurePartnerOption } from "@inputs/autocomplete/AutocompleteStructurePartners";
import { useGlobalContext } from "@root/context";
import { OfferKind } from "@root/utils/enums/offer";
import { useI18nContext } from "@root/utils/organisation";
import { useMemo } from "react";
import type { FieldValues } from "react-hook-form";
import { useAIGeneration } from "../logics";
import { parseSocialOfferFromAI, useCreateSocialOffers } from "./logics";
import { socialOfferFormSchema } from "./schema";

export type TSocialOfferForm = Override<
  TSocialOfferCreationOrEdition,
  {
    permanent: "permanent" | "temporary";
    serviceDates?: { startDate?: string; endDate?: string };
    tags?: { value: string; label: string }[];
    financingModes?: { value: string; label: string }[];
    provider: TStructurePartnerOption;
    contact?: TContactValue;
    perimeter?: TPerimeterOption[];
  }
>;

const LABELS: ICreateWithAIModalLabels = {
  title: "Importer du texte pour générer l'offre de service via IA",
  instruction:
    "Copier coller le texte de l'offre de service que vous avez trouvé dans l'encart ci-dessous puis cliquer sur « Afficher la/ les offres générées » pour remplir automatiquement les champs de création d'une offre de service.",
  generate: "Afficher la/les offres générées",
  confirm: "Créer",
  items: "Offres générées",
  generating: "Offre(s) en cours de génération",
  empty: "Aucune offre de service n'a été détectée dans ce texte.",
  failed: (failedCount, total) => `Échec de génération pour ${failedCount} offre(s) sur ${total}.`,
};

export interface IModalSocialOfferFromAIProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (items: ISocialOfferWithFavoriteChallenges[]) => void;
  defaultProvider?: TStructurePartnerOption;
}

export const ModalSocialOfferFromAI = ({ open, onClose, onCreated, defaultProvider }: IModalSocialOfferFromAIProps) => {
  const { settingsV2, challenges } = useGlobalContext();
  const { createSocialOffers, loading } = useCreateSocialOffers();
  const { phoneCode } = useI18nContext();

  // Same options as ModalSocialOffer: only tags linked to a challenge, grouped by challenge
  const tagOptions = useMemo(
    () =>
      challenges.flatMap((challenge) =>
        challenge.tags.flatMap((tag) => {
          const settingValue = settingsV2.statusSocialTag?.values.find((v) => v.value === tag);
          return settingValue ? [{ ...settingValue, group: challenge.name }] : [];
        })
      ),
    [challenges, settingsV2.statusSocialTag]
  );

  const parseItem = useMemo(
    () =>
      parseSocialOfferFromAI({
        tags: tagOptions,
        financingModes: settingsV2.financingModes?.values.filter((value) => value.active !== false) ?? [],
        defaultProvider,
        phoneCode,
      }),
    [tagOptions, settingsV2.financingModes, defaultProvider, phoneCode]
  );
  const { generation, handleGenerate, reset } = useAIGeneration(OfferKind.SOCIAL, parseItem);

  const context = useMemo(
    () => ({
      settings: {
        settings: {
          ...(settingsV2.financingModes
            ? { financingModes: { ...settingsV2.financingModes, settingType: "financingModes" } }
            : {}),
        },
        status: {
          ...(settingsV2.statusSocialTag
            ? {
                // biome-ignore lint/style/useNamingConvention: key must match backend settingType
                social_tag: {
                  ...settingsV2.statusSocialTag,
                  settingType: "social_tag",
                  values: tagOptions,
                },
              }
            : {}),
        },
      },
    }),
    [settingsV2.financingModes, settingsV2.statusSocialTag, tagOptions]
  );

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleConfirm = async (offers: TSocialOfferForm[]) => {
    const { created, failed, failedIndexes, structurePartnersCreated } = await createSocialOffers(offers);
    const failedMessage =
      failed.length > 0
        ? `Échec de création pour ${failed.length} offre(s) sur ${offers.length}, elle(s) reste(nt) dans la fenêtre.`
        : "";
    const createdMessage =
      created.length > 0
        ? `${created.length} offre(s) de service créée(s).${
            structurePartnersCreated > 0
              ? ` ${structurePartnersCreated} nouveau(x) partenaire(s) créé(s) et ajouté(s) au tableau de bord des partenaires.`
              : ""
          }`
        : "";
    window.publish("feedback", {
      severity: failed.length === 0 ? "success" : created.length === 0 ? "error" : "warning",
      message: [createdMessage, failedMessage].filter(Boolean).join(" "),
    });
    if (created.length > 0) {
      onCreated?.(created);
    }
    if (failed.length === 0) {
      handleClose();
      return;
    }
    return failedIndexes;
  };

  return (
    <CreateWithAIModal<FieldValues, TSocialOfferForm>
      open={open}
      labels={LABELS}
      generation={generation}
      schema={socialOfferFormSchema}
      context={context}
      onClose={handleClose}
      onGenerate={handleGenerate}
      onConfirm={handleConfirm}
      loading={loading}
    />
  );
};
